import { inject } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivateFn, CanMatchFn, Route, Router, UrlSegment } from '@angular/router';
import { PermissionKey } from '../permissions/permission.types';
import { AccessCacheService } from '../services/access-cache.service';
import { OrgContextService } from '../services/org-context.service';
import { PermissionMatrixStore } from '../services/permission-matrix.store';
import { PermissionsService } from '../services/permissions.service';
import { OrganizationSettingsService } from 'src/app/pages/organization-settings/services/organization-settings.service';

/**
 * Permission guard factory helpers for route protection.
 *
 * Use cases:
 * - Block entire lazy modules with `canMatchPermission('Org.Billing.ManagePlan')`.
 * - Block specific routes with `canActivatePermission('Org.Audit.View')`.
 * - Optionally resolve resource IDs from route params for project/card scoped permissions.
 */
const NOT_AUTHORIZED_URL = '/access-denied';
const CRITICAL_ROUTE_PERMISSION_MAX_AGE_MS = 60 * 1000;

type ResourceIdResolver = (route: ActivatedRouteSnapshot) => string | undefined;

/**
 * Route guard to protect specific routes by permission.
 *
 * Example:
 * - `canActivate: [canActivatePermission('Org.Audit.View')]`
 * - `canActivate: [canActivatePermission('Project.Edit', r => r.params['projectId'])]`
 */
export const canActivatePermission = (
  permission: PermissionKey,
  getResourceId?: ResourceIdResolver
): CanActivateFn => {
  return async (route: ActivatedRouteSnapshot) => {
    const permissions = inject(PermissionsService);
    const orgContext = inject(OrgContextService);
    const accessCache = inject(AccessCacheService);
    const matrixStore = inject(PermissionMatrixStore);
    const organizationSettings = inject(OrganizationSettingsService, { optional: true });
    const router = inject(Router);
    await organizationSettings?.loadOrganizationsAndSetCurrent();
    await orgContext.ensureOrgContextReady();
    await matrixStore.preloadCurrentOrg({
      requireFresh: true,
      maxAgeMs: CRITICAL_ROUTE_PERMISSION_MAX_AGE_MS
    });
    const resourceId = getResourceId ? getResourceId(route) : undefined;
    const definition = matrixStore.getPermissionDefinition(permission);
    if (resourceId && definition?.scope === 'project') {
      await accessCache.primeProjects([resourceId]);
    }
    if (resourceId && definition?.scope === 'card') {
      await accessCache.primeCards([resourceId]);
    }
    const allowed = permissions.can(permission, resourceId);
    return allowed ? true : router.parseUrl(NOT_AUTHORIZED_URL);
  };
};

/**
 * Route guard to prevent lazy modules from loading without permission.
 *
 * Example:
 * - `canMatch: [canMatchPermission('Org.Billing.ManagePlan')]`
 */
export const canMatchPermission = (
  permission: PermissionKey
): CanMatchFn => {
  return async (_route: Route, _segments: UrlSegment[]) => {
    const permissions = inject(PermissionsService);
    const orgContext = inject(OrgContextService);
    const matrixStore = inject(PermissionMatrixStore);
    const organizationSettings = inject(OrganizationSettingsService, { optional: true });
    const router = inject(Router);
    await organizationSettings?.loadOrganizationsAndSetCurrent();
    await orgContext.ensureOrgContextReady();
    await matrixStore.preloadCurrentOrg({
      requireFresh: true,
      maxAgeMs: CRITICAL_ROUTE_PERMISSION_MAX_AGE_MS
    });
    const allowed = permissions.can(permission);
    return allowed ? true : router.parseUrl(NOT_AUTHORIZED_URL);
  };
};
